var AmazonCognitoIdentity = require('amazon-cognito-identity-js');

const COGNITO_COOKIE_PREFIX = 'CognitoIdentityServiceProvider';
const LOGIN_TIMESTAMP_KEY = 'cognito_login_timestamp';
const SESSION_DURATION_KEY = 'cognito_session_duration_hours';
const DEFAULT_SESSION_DURATION_HRS = 24;

// Renew a little ahead of expiry so a request never lands on a just-dead token.
const REFRESH_LEAD_MS = 5 * 60 * 1000;
const MIN_REFRESH_DELAY_MS = 30 * 1000;

export const getSessionDurationHrs = (settings) => (
    settings?.cognito?.session_duration || DEFAULT_SESSION_DURATION_HRS
);

// Tokens live in cookies rather than localStorage so the Express layer can read
// them when authorising /media/* requests.
export const buildCookieStorage = (settings) => new AmazonCognitoIdentity.CookieStorage({
    domain: window.location.hostname,
    expires: getSessionDurationHrs(settings) / 24,
});

export const buildUserPool = (settings) => new AmazonCognitoIdentity.CognitoUserPool({
    UserPoolId: settings?.cognito?.user_pool_id,
    ClientId: settings?.cognito?.app_client_id,
    Storage: buildCookieStorage(settings),
});

export const recordSignIn = (sessionDurationHrs) => {
    localStorage.setItem(LOGIN_TIMESTAMP_KEY, Date.now().toString());
    localStorage.setItem(SESSION_DURATION_KEY, sessionDurationHrs.toString());
};

export const clearSessionMarkers = () => {
    localStorage.removeItem(LOGIN_TIMESTAMP_KEY);
    localStorage.removeItem(SESSION_DURATION_KEY);
};

// auth_time marks the original sign-in and survives every refresh untouched,
// which makes it the right anchor for an absolute session ceiling. The stored
// timestamp is only a fallback for sessions that predate this.
const getAuthTimeMs = (session) => {
    const authTime = session?.getAccessToken?.()?.decodePayload?.()?.auth_time;

    if (authTime) {
        return authTime * 1000;
    }

    const storedTimestamp = localStorage.getItem(LOGIN_TIMESTAMP_KEY);

    return storedTimestamp ? parseInt(storedTimestamp, 10) : null;
};

const isBeyondSessionCap = (session, sessionDurationHrs) => {
    const authTimeMs = getAuthTimeMs(session);

    if (!authTimeMs) {
        return true;
    }

    return (Date.now() - authTimeMs) > (sessionDurationHrs * 60 * 60 * 1000);
};

// getSession swaps an expired access token for a new one using the refresh
// token, and writes the result straight back into cookie storage.
const getSession = (cognitoUser) => new Promise((resolve, reject) => {
    cognitoUser.getSession((error, session) => {
        if (error) {
            reject(error);
        } else {
            resolve(session);
        }
    });
});

// Read straight from cookie storage, because a user returned by getCurrentUser
// has no session attached yet and would otherwise look like a fresh refresh.
const readStoredAccessToken = (settings, username) => buildCookieStorage(settings).getItem(
    `${COGNITO_COOKIE_PREFIX}.${settings.cognito.app_client_id}.${username}.accessToken`
) || null;

const endSession = (cognitoUser) => {
    if (cognitoUser) {
        cognitoUser.signOut();
    }

    clearSessionMarkers();

    return null;
};

/**
 * Resolves the current Cognito session, refreshing the access token when it has
 * expired.
 *
 * session_duration is an absolute ceiling measured from the original sign-in, so
 * a refresh can extend the access token but never the session itself.
 *
 * @param {Object} settings - Site settings containing the cognito block
 * @returns {Promise<?{ session: Object, refreshed: boolean }>} The live session,
 * or null when the user needs to sign in again.
 */
export const resolveSession = async (settings) => {
    if (!settings?.cognito) {
        return null;
    }

    const cognitoUser = buildUserPool(settings).getCurrentUser();

    if (!cognitoUser) {
        clearSessionMarkers();
        return null;
    }

    const previousAccessToken = readStoredAccessToken(settings, cognitoUser.getUsername());
    let session = null;

    try {
        session = await getSession(cognitoUser);
    } catch (error) {
        return endSession(cognitoUser);
    }

    if (!session?.isValid() || isBeyondSessionCap(session, getSessionDurationHrs(settings))) {
        return endSession(cognitoUser);
    }

    return {
        session,
        refreshed: session.getAccessToken().getJwtToken() !== previousAccessToken,
    };
};

/**
 * Milliseconds to wait before renewing the session again, so a page left open
 * keeps its cookies fresh instead of failing the next request it makes.
 *
 * @param {Object} session - A valid CognitoUserSession
 * @returns {number}
 */
export const msUntilRefresh = (session) => {
    const expiresAtMs = session.getAccessToken().getExpiration() * 1000;

    return Math.max(expiresAtMs - Date.now() - REFRESH_LEAD_MS, MIN_REFRESH_DELAY_MS);
};

/**
 * Re-issues the CloudFront signed cookies, whose lifetime has to keep pace with
 * the Cognito session for media requests to stay authorised.
 *
 * @param {Object} settings - Site settings containing the cognito block
 * @returns {Promise<void>}
 */
export const refreshSignedCookies = async (settings) => {
    if (!settings?.cognito?.signed_cookies) {
        return;
    }

    try {
        await fetch('/get-signed-cookie/', { method: 'GET', credentials: 'include' });
    } catch (error) {
        console.error('Error refreshing signed cookies:', error);
    }
};
